<template>
	<div
		ref="nav"
		class="ui fixed inverted stackable pointing menu"
		:class="{'transparent': $route.name === 'home' && clientSize.clientWidth > 768}">
		<div class="ui container">
			<router-link to="/">
				<h3 class="ui header item m-blue">{{ blogName }}</h3>
			</router-link>

			<router-link to="/home" class="item" :class="itemClass('home')">
				<i class="home icon"></i>首页
			</router-link>

			<el-dropdown trigger="click" @command="categoryRoute">
				<span class="el-dropdown-link item" :class="itemClass('category')">
					<i class="idea icon"></i>分类<i class="caret down icon"></i>
				</span>
				<el-dropdown-menu slot="dropdown">
					<el-dropdown-item
						v-for="(category,index) in categoryList"
						:key="index"
						:command="category.name">
						{{ category.name }}
					</el-dropdown-item>
				</el-dropdown-menu>
			</el-dropdown>

			<router-link to="/archives" class="item" :class="itemClass('archives')">
				<i class="clone icon"></i>归档
			</router-link>

			<router-link to="/moments" class="item" :class="itemClass('moments')">
				<i class="comment alternate outline icon"></i>动态
			</router-link>

			<router-link v-if="!isLoggedIn" to="/login" class="item" :class="itemClass('login')">
				<i class="sign-in alternate icon"></i>登录
			</router-link>

			<el-autocomplete
				v-model="queryString"
				:fetch-suggestions="debounceQuery"
				placeholder="搜索..."
				class="right item m-search"
				:class="{'m-mobile-hide': mobileHide}"
				popper-class="m-search-item"
				@select="handleSelect">
				<i class="search icon el-input__icon" slot="suffix"></i>
				<template slot-scope="{ item }">
					<div class="title">{{ item.title }}</div>
					<span class="content">{{ item.content }}</span>
				</template>
			</el-autocomplete>

			<div class="item user-avatar-container" :class="{'m-mobile-hide': mobileHide}" v-if="isLoggedIn">
				<el-dropdown trigger="click" @command="onUserMenuCommand">
					<div class="avatar-wrapper">
						<img :src="userAvatar" alt="avatar" class="user-avatar">
						<span class="user-name">{{ nickname }}</span>
						<i class="caret down icon"></i>
					</div>
					<el-dropdown-menu slot="dropdown" class="user-dropdown">
						<div class="user-info">
							<img :src="userAvatar" alt="avatar" class="dropdown-avatar">
							<div class="user-details">
								<div class="username">{{ nickname }}</div>
								<div class="user-role">{{ roleLabel }}</div>
							</div>
						</div>
						<el-dropdown-item command="center">
							<i class="el-icon-user"></i> 个人中心
						</el-dropdown-item>
						<el-dropdown-item command="settings">
							<i class="el-icon-setting"></i> 设置
						</el-dropdown-item>
						<el-dropdown-item command="logout" divided>
							<i class="el-icon-switch-button"></i> 退出登录
						</el-dropdown-item>
					</el-dropdown-menu>
				</el-dropdown>
			</div>

			<button class="ui menu black icon button m-right-top m-mobile-show" @click="toggle">
				<i class="sidebar icon"></i>
			</button>
		</div>
	</div>
</template>

<script>
import {getSearchBlogList} from '@/api/blog'
import {mapState} from 'vuex'
import {clearViewAuth, getViewUser, isViewLoggedIn} from '@/util/viewAuth'

export default {
	name: 'Nav',
	props: {
		blogName: {
			type: String,
			required: true
		},
		categoryList: {
			type: Array,
			required: true
		},
	},
	data() {
		return {
			mobileHide: true,
			queryString: '',
			queryResult: [],
			timer: null,
			isLoggedIn: false,
			nickname: '',
			role: '',
			userAvatar: '/img/avatar.jpg'
		}
	},
	computed: {
		...mapState(['clientSize']),
		roleLabel() {
			if (this.role === 'ROLE_admin') {
				return '管理员'
			}
			return this.role || '用户'
		}
	},
	watch: {
		'$route.path'() {
			this.mobileHide = true
			this.syncAuthState()
		}
	},
	mounted() {
		this.handleScroll = () => {
			if (this.$route.name === 'home' && this.clientSize.clientWidth > 768) {
				if (window.scrollY > this.clientSize.clientHeight / 2) {
					this.$refs.nav.classList.remove('transparent')
				} else {
					this.$refs.nav.classList.add('transparent')
				}
			}
		}
		this.handleDocumentClick = e => {
			const isNavChild = this.$refs.nav.contains(e.target)
			if (!this.mobileHide && !isNavChild) {
				this.mobileHide = true
			}
		}
		this.handleStorage = () => this.syncAuthState()
		this.handleAuthChanged = () => this.syncAuthState()
		window.addEventListener('scroll', this.handleScroll)
		document.addEventListener('click', this.handleDocumentClick)
		window.addEventListener('storage', this.handleStorage)
		window.addEventListener('view-auth-changed', this.handleAuthChanged)
		this.syncAuthState()
	},
	beforeDestroy() {
		window.removeEventListener('scroll', this.handleScroll)
		document.removeEventListener('click', this.handleDocumentClick)
		window.removeEventListener('storage', this.handleStorage)
		window.removeEventListener('view-auth-changed', this.handleAuthChanged)
	},
	methods: {
		itemClass(name) {
			return {
				'm-mobile-hide': this.mobileHide,
				'active': this.$route.name === name
			}
		},
		syncAuthState() {
			this.isLoggedIn = isViewLoggedIn()
			if (!this.isLoggedIn) {
				this.nickname = ''
				this.role = ''
				this.userAvatar = '/img/avatar.jpg'
				return
			}
			const user = getViewUser()
			this.nickname = user.nickname
			this.role = user.role
			this.userAvatar = user.avatar || '/img/avatar.jpg'
		},
		toggle() {
			this.mobileHide = !this.mobileHide
		},
		categoryRoute(name) {
			this.$router.push(`/category/${name}`)
		},
		debounceQuery(queryString, callback) {
			this.timer && clearTimeout(this.timer)
			this.timer = setTimeout(() => this.querySearchAsync(queryString, callback), 1000)
		},
		querySearchAsync(queryString, callback) {
			if (!queryString || queryString.trim() === '' || queryString.trim().length > 20) {
				return
			}
			getSearchBlogList(queryString).then(res => {
				if (res.code !== 200) {
					return
				}
				this.queryResult = res.data || []
				if (this.queryResult.length === 0) {
					this.queryResult.push({title: '暂无结果'})
				}
				callback(this.queryResult)
			}).catch(() => {
				this.msgError('请求失败')
			})
		},
		handleSelect(item) {
			if (item.id) {
				this.$router.push(`/blog/${item.id}`)
			}
		},
		onUserMenuCommand(command) {
			if (command === 'center') {
				this.$router.push('/user/center')
				return
			}
			if (command === 'settings') {
				this.$router.push('/user/settings')
				return
			}
			if (command === 'logout') {
				clearViewAuth()
				this.syncAuthState()
				this.$router.push('/login')
				this.msgSuccess('已退出登录')
			}
		}
	}
}
</script>

<style>
.ui.fixed.menu .container {
	width: 1400px !important;
	margin-left: auto !important;
	margin-right: auto !important;
}

.ui.fixed.menu {
	transition: .3s ease-out;
}

.ui.inverted.pointing.menu.transparent {
	background: transparent !important;
}

.ui.inverted.pointing.menu.transparent .active.item:after {
	background: transparent !important;
	transition: .3s ease-out;
}

.ui.inverted.pointing.menu.transparent .active.item:hover:after {
	background: transparent !important;
}

.el-dropdown-link {
	outline-style: none !important;
	outline-color: unset !important;
	height: 100%;
	cursor: pointer;
}

.el-dropdown-menu {
	margin: 7px 0 0 0 !important;
	padding: 0 !important;
	border: 0 !important;
	background: #1b1c1d !important;
}

.el-dropdown-menu__item {
	padding: 0 15px !important;
	color: rgba(255, 255, 255, .9) !important;
}

.el-dropdown-menu__item:hover {
	background: rgba(255, 255, 255, .08) !important;
}

.el-popper .popper__arrow::after {
	content: none !important;
}

.popper__arrow {
	display: none !important;
}

.m-search {
	min-width: 220px;
	padding: 0 !important;
}

.m-search input {
	color: rgba(255, 255, 255, .9);
	border: 0 !important;
	background-color: inherit;
	padding: .67857143em 2.1em .67857143em 1em;
}

.m-search i {
	color: rgba(255, 255, 255, .9) !important;
}

.m-search-item {
	min-width: 350px !important;
}

.m-search-item li {
	line-height: normal !important;
	padding: 8px 10px !important;
}

.m-search-item li .title {
	text-overflow: ellipsis;
	overflow: hidden;
	color: rgba(0, 0, 0, 0.87);
}

.m-search-item li .content {
	text-overflow: ellipsis;
	font-size: 12px;
	color: rgba(0, 0, 0, .70);
}

.user-avatar-container {
	margin-left: 10px !important;
	padding: 0 !important;
}

.avatar-wrapper {
	cursor: pointer;
	position: relative;
	padding: 0 4px !important;
	display: inline-flex;
	align-items: center;
	gap: 6px;
}

.user-avatar {
	width: 34px;
	height: 34px;
	border-radius: 50%;
	object-fit: cover;
	border: 2px solid rgba(255, 255, 255, 0.5);
	transition: all 0.3s;
}

.avatar-wrapper .user-name {
	font-size: 13px;
	color: rgba(255, 255, 255, .9);
	max-width: 90px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.avatar-wrapper:hover .user-avatar {
	border-color: #fff;
	transform: scale(1.03);
}

.user-dropdown {
	min-width: 220px !important;
}

.user-info {
	padding: 15px !important;
	display: flex;
	align-items: center;
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.dropdown-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	margin-right: 10px;
	object-fit: cover;
}

.user-details {
	display: flex;
	flex-direction: column;
}

.username {
	font-weight: bold;
	color: rgba(255, 255, 255, 0.9) !important;
	margin-bottom: 3px;
}

.user-role {
	font-size: 12px;
	color: rgba(255, 255, 255, 0.7) !important;
}

.el-dropdown-menu__item i {
	margin-right: 5px;
}
</style>
